import { useMemo } from 'react'
import { C, colHead } from '../theme'
import { grp, usd } from '../lib/format'
import { Action, BlockHead, Chip, Drawer, NoData, Note, Px, Strong } from './ui/primitives'
import { LightweightPriceChart } from './ui/LightweightPriceChart'
import { MARKETS, MARKET_META, PROBE_SIZES } from '../data/mock/constants'
import { buildDepth, buildSeries, ourTwoSided, usdEquivalent } from '../data/market'
import { useLiveChart, type ChartPoint } from '../data/liveChart'
import type { Market, Snapshot } from '../types'

const DEPTH_GRID = '0.9fr 1.1fr 1.1fr 1.1fr 1.1fr'

/**
 * Синтетика — запасной путь, когда VITE_LIVE_CHART_BASE_URL не задан
 * (локальная разработка без бэкенда). Живые ряды уже в абсолютной цене —
 * синтетику переводим из bps в те же единицы через meta.ref, чтобы график
 * не менял смысл осей между двумя источниками.
 */
function syntheticSeries(market: Market, tick: number) {
  const meta = MARKET_META[market]
  const raw = buildSeries(market, tick)
  const at = (bps: number) => meta.ref * (1 + bps / 10_000)
  const toPoints = (pick: (p: (typeof raw)[number]) => number | null): ChartPoint[] =>
    raw
      .map((p, i) => {
        const v = pick(p)
        return v == null ? null : { time: i, value: at(v) }
      })
      .filter((p): p is ChartPoint => p != null)
  return {
    ourBid: toPoints((p) => p.ourBidBps),
    ourAsk: toPoints((p) => p.ourAskBps),
    binanceBid: toPoints((p) => -p.binanceHalfSpreadBps),
    binanceAsk: toPoints((p) => p.binanceHalfSpreadBps),
    uniswapBid: toPoints((p) => p.uniBidBps),
    uniswapAsk: toPoints((p) => p.uniAskBps),
  }
}

export function PriceChart({
  snap,
  market,
  onMarket,
  size,
  onSize,
  depthOpen,
  onToggleDepth,
  showNotes,
}: {
  snap: Snapshot
  market: Market
  onMarket: (m: Market) => void
  size: number
  onSize: (s: number) => void
  depthOpen: boolean
  onToggleDepth: () => void
  showNotes: boolean
}) {
  const meta = MARKET_META[market]
  const depth = useMemo(() => buildDepth(market), [market])
  // Две стороны собраны из двух разных направлений — см. ourTwoSided.
  const two = useMemo(() => ourTwoSided(market), [market])

  const live = useLiveChart(market)
  const fallback = useMemo(
    () => syntheticSeries(market, Math.floor(snap.blockAgeSec / 4)),
    [market, snap.blockAgeSec],
  )
  const chartSeries = live
    ? {
        ourBid: live.ourBid,
        ourAsk: live.ourAsk,
        binanceBid: live.binanceBid,
        binanceAsk: live.binanceAsk,
        uniswapBid: live.uniswapBid,
        uniswapAsk: live.uniswapAsk,
      }
    : fallback
  const trades = live?.trades ?? []

  const sizes = PROBE_SIZES[market]
  const isEth = meta.ref > 100

  return (
    <div style={{ minWidth: 0 }}>
      <BlockHead
        title="Our price vs Binance"
        right={
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <span style={{ display: 'flex', gap: 4 }}>
              {MARKETS.map((m) => (
                <Chip
                  key={m}
                  active={m === market}
                  pad="2px 8px"
                  onClick={() => {
                    onMarket(m)
                    onSize(PROBE_SIZES[m][Math.floor(PROBE_SIZES[m].length / 2)])
                  }}
                >
                  {m}
                </Chip>
              ))}
            </span>
            <Action onClick={onToggleDepth}>{depthOpen ? 'hide depth' : 'depth by size'}</Action>
          </span>
        }
      />

      {/* Размер не опционален: и Binance, и Uniswap существуют только под
          конкретный размер, а «bid/ask без размера» — величина, которой
          алгоритм не пользуется. Пробы в базовых единицах рынка. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          marginTop: 12,
        }}
      >
        <span style={{ ...colHead, paddingRight: 4 }}>Size</span>
        {sizes.map((s) => (
          <Chip key={s} active={s === size} pad="1px 8px" onClick={() => onSize(s)}>
            {isEth ? `${s} ETH` : grp(s)}
          </Chip>
        ))}
        <span style={{ fontSize: 13, color: C.muted, paddingLeft: 4, whiteSpace: 'nowrap' }}>
          ≈ {usd(usdEquivalent(market, size))}
        </span>
      </div>

      <div style={{ marginTop: 12 }}>
        <LightweightPriceChart height={220} series={chartSeries} trades={trades} />
      </div>

      <div
        style={{
          display: 'flex',
          gap: 18,
          rowGap: 4,
          flexWrap: 'wrap',
          fontSize: 13,
          color: C.muted2,
          marginTop: 10,
        }}
      >
        <span style={{ whiteSpace: 'nowrap' }}>
          <span
            style={{ display: 'inline-block', width: 16, height: 2, background: C.accent, verticalAlign: 'middle' }}
          />{' '}
          our bid and ask{two ? ` · ${two.spreadBps.toFixed(2)} bps apart` : ''}
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>
          <span
            style={{ display: 'inline-block', width: 16, height: 1, background: '#9A9A9A', verticalAlign: 'middle' }}
          />{' '}
          Binance top of book
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>
          <span
            style={{
              display: 'inline-block',
              width: 16,
              height: 0,
              borderTop: `1px dashed ${C.uni}`,
              verticalAlign: 'middle',
            }}
          />{' '}
          Uniswap fee spread, sampled once per block
        </span>
        <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          {live ? 'live · last 15 min' : 'simulated — no live chart backend configured'} · {meta.sizeLabel}
        </span>
      </div>

      {depthOpen && (
        <Drawer marginTop={16}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              rowGap: 4,
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 700 }}>Depth by size · {market}</span>
            <span style={{ fontSize: 13, color: C.muted }}>{meta.bookAgeLabel}</span>
          </div>
          <div
            style={{
              ...colHead,
              display: 'grid',
              gridTemplateColumns: DEPTH_GRID,
              gap: 12,
              borderBottom: `1px solid ${C.border}`,
              padding: '10px 0 8px',
            }}
          >
            <span>Size</span>
            <span style={{ textAlign: 'right' }}>Binance bid</span>
            <span style={{ textAlign: 'right' }}>Binance ask</span>
            <span style={{ textAlign: 'right', color: C.uni }}>Uni bid</span>
            <span style={{ textAlign: 'right', color: C.uni }}>Uni ask</span>
          </div>
          {depth.map((d) => {
            const sel = d.size === size
            return (
              <div
                key={d.size}
                onClick={() => onSize(d.size)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: DEPTH_GRID,
                  gap: 12,
                  alignItems: 'baseline',
                  padding: '9px 0',
                  borderBottom: `1px solid ${C.rule}`,
                  fontSize: 15,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  background: sel ? C.accentWash : 'transparent',
                }}
              >
                <span style={{ color: sel ? C.accent : C.text, fontWeight: sel ? 600 : 400 }}>
                  {d.sizeLabel}
                </span>
                <span style={{ textAlign: 'right' }}>
                  <Px value={d.binanceBid} />
                </span>
                <span style={{ textAlign: 'right' }}>
                  <Px value={d.binanceAsk} />
                </span>
                <span style={{ textAlign: 'right', color: C.uni }}>
                  {d.uniBid == null ? <NoData /> : <Px value={d.uniBid} tailColor={C.faint} />}
                </span>
                <span style={{ textAlign: 'right', color: C.uni }}>
                  {d.uniAsk == null ? <NoData /> : <Px value={d.uniAsk} tailColor={C.faint} />}
                </span>
              </div>
            )
          })}
          {showNotes && (
            <Note>
              Each Binance row is a <Strong>VWAP sweep of the book at that size</Strong>, not
              top-of-book — comparing top-of-book with itself is why the old dashboard's slippage
              column was structurally zero. Uniswap has no bid and no ask of its own: both sides are
              two independent exact-size swaps simulated on one pinned block. They are{' '}
              <Strong>not quoted per row</Strong> — our quoter fans out across four fee tiers, so
              one price costs four eth_calls and this table alone would cost 216 per refresh,
              against a shared daily key budget of 13M. The whole curve comes instead from pool
              state read once per block and evaluated locally, in the collector rather than in the
              browser.
            </Note>
          )}
        </Drawer>
      )}
    </div>
  )
}
